import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MoreOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { FooterButton } from "../FooterButton";
import { OptionsPopover, type SecondaryAction } from "../OptionsPopover";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../../hooks/useConfiguration";
import {
  buildFooterOrder,
  FOOTER_ZONE_ID,
  OPTIONS_KEY,
  splitFooterActions,
  type FooterBarItem,
} from "./footerPinning";
import { FOOTER_ROW_GAP_CLASS, useFooterDensity } from "./useFooterDensity";
import { useFooterDrag } from "./useFooterDrag";
import { PinnedFooterAction } from "./PinnedFooterAction";
import { FooterDragPreview } from "./FooterDragPreview";
import { FooterMeasureRow } from "./FooterMeasureRow";

const NO_FOOTER_ACTIONS: readonly string[] = [];

interface TableFooterBarProps {
  /** Нова · Добави · Закрий — в обичайния им ред, само тези с право
   *  (ключовете са `MAIN_FOOTER_KEYS`). „Опции" се добавя тук. */
  mainButtons: FooterBarItem[];
  secondaryActions: SecondaryAction[];
  /** Вляво в лентата, извън подредбата (часовникът в режим „Схема"); само
   *  когато лентата се подрежда (не на телефон). */
  leading?: ReactNode;
}

/** Цялата лента е зона за пускане — по-лесно се улучва от реда с бутоните. */
const FooterDropZone = ({
  barRef,
  className,
  sideInset,
  children,
}: {
  barRef: React.RefObject<HTMLDivElement | null>;
  className: string;
  // Отстъпът от двете страни, когато вляво стои часовникът (иначе — px-5).
  sideInset?: number;
  children: React.ReactNode;
}) => {
  const { setNodeRef } = useDroppable({ id: FOOTER_ZONE_ID });
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        barRef.current = node;
      }}
      className={className}
      // Измерената ширина на часовника — известна чак по време на изпълнение
      // (правило 3, изключение 1).
      style={sideInset !== undefined ? { paddingInline: sideInset } : undefined}
    >
      {children}
    </div>
  );
};

/**
 * Лентата под масите: основните бутони (Нова · Добави · Закрий · Опции) и
 * изнесените от „Опции" действия, в личната подредба на акаунта
 * (`footerActions`). Докато „Опции" е отворен, плочките се влачат в лентата —
 * на всяко място, и пред, и между основните — а изнесените бутони обратно в
 * панела или на друго място; основните се разместват само в лентата. Ограничение за броя няма: при теснота бутоните
 * се свиват на стъпки (`useFooterDensity`), накрая текстът се реже с „…".
 * Часовникът (`TableRail`) и „Обобщено" (`OrderDetails`) са над лентата; в
 * режим „Схема" часовникът е вляво В лентата (`leading`).
 * На телефон лентата е фиксирана решетка 4×1 и не се подрежда.
 */
export const TableFooterBar = ({
  mainButtons,
  secondaryActions,
  leading,
}: TableFooterBarProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isNarrow, isDesktop } = useViewport();
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  // `?? []` — самоличност от кеша отпреди полето да съществува (docs/auth-access.md).
  const storedKeys = configuration.footerActions ?? NO_FOOTER_ACTIONS;

  // Универсален popover „Опции" — събира вторичните действия на всяка ширина.
  const [showMore, setShowMore] = useState(false);
  const closeOptions = () => setShowMore(false);

  // Escape затваря popover-а (кликът извън него минава през backdrop-а).
  useEffect(() => {
    if (!showMore) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        // на общия терминал (SharedAccountGate) — само затваря popover-а.
        e.preventDefault();
        setShowMore(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showMore]);

  const arrangeable = !isNarrow;

  const optionsItem: FooterBarItem | null =
    secondaryActions.length > 0
      ? {
          key: OPTIONS_KEY,
          button: {
            icon: <MoreOutlined />,
            label: t("ordering.options"),
            onClick: () => setShowMore((previous) => !previous),
            active: showMore,
          },
        }
      : null;
  const mainItems = optionsItem ? [...mainButtons, optionsItem] : mainButtons;
  const mainKeys = mainItems.map((item) => item.key);

  const { pinned, inOptions } = arrangeable
    ? splitFooterActions(secondaryActions, storedKeys)
    : { pinned: [], inOptions: secondaryActions };
  const orderKeys = arrangeable
    ? buildFooterOrder(
        storedKeys,
        mainKeys,
        pinned.map((action) => action.key),
      )
    : mainKeys;

  // Всеки бутон, който може да се появи в лентата, по ключ.
  const secondaryKeys = new Set(secondaryActions.map((action) => action.key));
  const itemByKey = new Map<string, FooterBarItem>(
    mainItems.map((item) => [item.key, item]),
  );
  for (const action of secondaryActions) {
    itemByKey.set(action.key, {
      key: action.key,
      button: {
        icon: action.icon,
        label: action.label,
        onClick: action.onClick,
        disabled: action.disabled,
        shortcut: action.shortcut,
        lockedTooltip: action.lockedTooltip,
      },
    });
  }

  const footerDensity = useFooterDensity(arrangeable);
  const footerDrag = useFooterDrag({
    orderKeys,
    storedKeys,
    barRef: footerDensity.barRef,
    onSave: (keys) =>
      updateUserConfiguration(
        { footerActions: keys },
        { onError: () => message.error(t("ordering.footerLayoutSaveError")) },
      ),
  });
  const displayItems = footerDrag.footerItems.flatMap(({ key, state }) => {
    const item = itemByKey.get(key);
    return item ? [{ item, state }] : [];
  });
  const density = footerDensity.density;
  const draggingSecondaryFromFooter =
    footerDrag.activeSource === "footer" &&
    !!footerDrag.activeKey &&
    secondaryKeys.has(footerDrag.activeKey);
  const activeItem = footerDrag.activeKey
    ? itemByKey.get(footerDrag.activeKey)
    : undefined;

  // При отворени „Опции" редът е над backdrop-а (заради влаченето), но
  // кликът по бутон в него само затваря панела — както преди, когато
  // backdrop-ът покриваше лентата. „Опции" сам си превключва панела.
  const closeOptionsInsteadOfClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(`[data-footer-item="${OPTIONS_KEY}"]`)) return;
    event.stopPropagation();
    closeOptions();
  };

  // Мишка: влачене след 6px. Тъч: задържане 200ms — иначе обикновеното
  // докосване на плочка би тръгнало да я влачи вместо да я натисне.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      // Нищо тук не се скролва — без автоскрол при влачене до ръба на екрана.
      autoScroll={false}
      onDragStart={footerDrag.handleDragStart}
      onDragMove={footerDrag.handleDragMove}
      onDragEnd={footerDrag.handleDragEnd}
      onDragCancel={footerDrag.handleDragCancel}
    >
      <FooterDropZone
        barRef={footerDensity.barRef}
        sideInset={arrangeable && leading ? footerDensity.sideInset : undefined}
        className={`w-full bg-pos-panel border-t border-pos-line relative ${
          isNarrow
            ? "grid grid-cols-4 gap-1.5 px-2.5 pt-2 pb-[calc(8px+env(safe-area-inset-bottom,0px)*0.5)]"
            : "flex items-center justify-center px-5 py-3"
        }`}
      >
        {/* Без data-footer-item — не участва в подредбата и влаченето */}
        {arrangeable && leading && (
          <div
            ref={footerDensity.leadingRef}
            className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2"
          >
            {leading}
          </div>
        )}
        {isNarrow ? (
          mainItems.map((item) => (
            <FooterButton key={item.key} {...item.button} />
          ))
        ) : (
          // При отворени „Опции" редът е НАД backdrop-а (z-30), за да могат
          // изнесените бутони да се хванат и върнат.
          <div
            className={`flex min-w-0 max-w-full items-center ${
              FOOTER_ROW_GAP_CLASS[density]
            } ${showMore ? "relative z-40" : ""}`}
            onClickCapture={showMore ? closeOptionsInsteadOfClick : undefined}
          >
            {/* Всички бутони — и основните — се местят, докато „Опции" е
                отворен; основните само в лентата (не се крият). */}
            {displayItems.map(({ item, state }) => (
              <PinnedFooterAction
                key={item.key}
                item={item}
                state={state}
                density={density}
                draggable={showMore}
              />
            ))}
          </div>
        )}

        {arrangeable && (
          <FooterMeasureRow
            ref={footerDensity.measureRef}
            items={displayItems
              .filter(({ state }) => state !== "hidden")
              .map(({ item }) => item)}
          />
        )}

        {showMore && secondaryActions.length > 0 && (
          <OptionsPopover
            actions={inOptions}
            isNarrow={isNarrow}
            isDesktop={isDesktop}
            onClose={closeOptions}
            arrangeable={arrangeable}
            draggedKey={
              footerDrag.activeSource === "options"
                ? footerDrag.activeKey
                : undefined
            }
            // Основен бутон не се връща в „Опции" — панелът не е цел за него.
            draggingFromFooter={draggingSecondaryFromFooter}
            isDropTarget={draggingSecondaryFromFooter && footerDrag.overOptions}
          />
        )}
      </FooterDropZone>

      {/* Без анимация при пускане — бутонът вече е на новото си място. */}
      <DragOverlay dropAnimation={null}>
        {activeItem && <FooterDragPreview item={activeItem} />}
      </DragOverlay>
    </DndContext>
  );
};
